import React, { useEffect, useState } from "react";
import api, { fmt } from "@/lib/api";
import { PageHeader } from "@/components/layout/PageHeader";
import { Package, Buildings, MapPin, ClipboardText, Truck, Wrench, Coins, Archive, ChartPie } from "@phosphor-icons/react";

const KPI = ({ icon: Icon, label, value, sub, testid, accent }) => (
  <div className="border border-gray-200 bg-white p-5" data-testid={testid}>
    <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 mb-3">
      {Icon && <Icon size={14} weight="bold" className={accent || "text-[#0020C2]"} />}
      <span>{label}</span>
    </div>
    <div className="text-3xl font-black tracking-tight">{value}</div>
    {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
  </div>
);

// Pure CSS-only donut using conic-gradient (no external chart library)
const StatusDonut = ({ inService, inStock, retired }) => {
  const total = inService + inStock + retired;
  if (!total) {
    return <div className="text-sm text-gray-500">No assets yet.</div>;
  }
  const svcPct = (inService / total) * 100;
  const stkPct = (inStock / total) * 100;
  const retPct = (retired / total) * 100;
  const bg = `conic-gradient(
    #15803D 0% ${svcPct}%,
    #2563EB ${svcPct}% ${svcPct + stkPct}%,
    #9CA3AF ${svcPct + stkPct}% ${svcPct + stkPct + retPct}%,
    #E5E7EB ${svcPct + stkPct + retPct}% 100%
  )`;
  return (
    <div className="flex items-center gap-6" data-testid="alm-status-donut">
      <div className="relative w-40 h-40 shrink-0" style={{ background: bg, borderRadius: "50%" }}>
        <div className="absolute inset-6 bg-white rounded-full flex flex-col items-center justify-center">
          <div className="text-2xl font-black tracking-tight">{total}</div>
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">Assets</div>
        </div>
      </div>
      <div className="space-y-2 text-sm">
        <Legend color="#15803D" label="In Service" value={inService} pct={svcPct} />
        <Legend color="#2563EB" label="In Stock" value={inStock} pct={stkPct} />
        <Legend color="#9CA3AF" label="Retired" value={retired} pct={retPct} />
      </div>
    </div>
  );
};

const Legend = ({ color, label, value, pct }) => (
  <div className="flex items-center gap-2">
    <span className="inline-block w-3 h-3" style={{ background: color }} />
    <span className="font-semibold w-24">{label}</span>
    <span className="font-mono text-xs text-gray-600">{value} · {pct.toFixed(0)}%</span>
  </div>
);

export default function AlmDashboardPage() {
  const [s, setS] = useState(null);

  const load = async () => {
    const { data } = await api.get("/alm/summary");
    setS(data);
  };
  useEffect(() => { load(); }, []);

  const exportPdf = async () => {
    const resp = await api.get("/alm/assets/export-pdf", { responseType: "blob" });
    const url = URL.createObjectURL(resp.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `asset-register-${new Date().toISOString().slice(0, 10)}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  if (!s) {
    return <div className="p-8 text-sm text-gray-500">Loading…</div>;
  }

  return (
    <div>
      <PageHeader
        eyebrow="Asset Lifecycle"
        title="ALM Dashboard"
        subtitle="Lifecycle KPIs across products, sites, locations, procurement, receipts, assets, work orders and retirements."
        testid="alm-dashboard-title"
        actions={
          <button onClick={exportPdf} data-testid="alm-export-pdf-btn" className="bg-[#0020C2] text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#001AA3] inline-flex items-center gap-2">
            <ChartPie size={14} weight="bold" /> Export Asset Register (PDF)
          </button>
        }
      />

      <div className="p-8 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          <KPI icon={Package} label="Assets" value={fmt(s.assets_total)} sub="Total tracked" testid="alm-kpi-assets" />
          <KPI icon={Package} label="In Service" value={fmt(s.assets_in_service)} sub="Capitalized + active" testid="alm-kpi-inservice" accent="text-green-700" />
          <KPI icon={Package} label="In Stock" value={fmt(s.assets_in_stock)} sub="Available for deployment" testid="alm-kpi-instock" accent="text-blue-700" />
          <KPI icon={Archive} label="Retired" value={fmt(s.assets_retired)} sub="Disposed / written off" testid="alm-kpi-retired" accent="text-gray-500" />
          <KPI icon={Wrench} label="Open Work Orders" value={fmt(s.open_work_orders)} sub="Maintenance pending" testid="alm-kpi-wo" accent="text-orange-600" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="border border-gray-200 bg-white p-6">
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 mb-4">Asset Status Mix</div>
            <StatusDonut inService={s.assets_in_service} inStock={s.assets_in_stock} retired={s.assets_retired} />
          </div>
          <div className="border border-gray-200 bg-white p-6">
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 mb-4">Masters &amp; Operations</div>
            <div className="grid grid-cols-2 gap-3">
              <MiniKPI icon={Package} label="Products" value={s.products} testid="alm-kpi-products" />
              <MiniKPI icon={Buildings} label="Sites" value={s.sites} testid="alm-kpi-sites" />
              <MiniKPI icon={MapPin} label="Locations" value={s.locations} testid="alm-kpi-locations" />
              <MiniKPI icon={ClipboardText} label="Plans" value={s.procurement_plans} testid="alm-kpi-plans" />
              <MiniKPI icon={Truck} label="Receipts" value={s.receipts} testid="alm-kpi-receipts" />
              <MiniKPI icon={Coins} label="Capitalized" value={s.assets_in_service} testid="alm-kpi-capitalized" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const MiniKPI = ({ icon: Icon, label, value, testid }) => (
  <div className="flex items-center justify-between border border-gray-100 px-3 py-3" data-testid={testid}>
    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-600">
      <Icon size={14} weight="bold" className="text-[#0020C2]" />
      <span>{label}</span>
    </div>
    <div className="text-xl font-black font-mono tracking-tight">{value}</div>
  </div>
);
